<script setup>
import { ref, watch, onMounted, onUnmounted } from 'vue'
import Hls from 'hls.js'

const props = defineProps({
  videoUrl: {
    type: [String, null],
    default: null,
  },
  coverImage: {
    type: [String, null],
    required: true,
  },
  title: String,
  category: String,
  size: {
    type: String,
    default: 'sm',
    validator: (value) => ['sm', 'lg', 'video'].includes(value),
  },
  autoplay: {
    type: Boolean,
    default: false,
  },
  disablePauseOnHover: {
    type: Boolean,
    default: false,
  },
  showVolumeControl: {
    type: Boolean,
    default: false,
  },
  loop: {
    type: Boolean,
    default: false,
  },
})

const videoRef = ref(null)
const coverRef = ref(null)
const rootRef = ref(null)
const isLoaded = ref(false)
const isInView = ref(false)
const scrollObserver = ref(null)
const lazyObserver = ref(null)
let mediaStarted = false

const isTouchDevice = () => {
  if (navigator.maxTouchPoints > 0) return true
  if (typeof window.matchMedia !== 'function') return false
  return window.matchMedia('(pointer: coarse)').matches
}

watch([isInView, isLoaded], ([inView, loaded]) => {
  if (!inView || !loaded || props.autoplay || props.size === 'video' || !isTouchDevice()) return
  if (!videoRef.value) return
  videoRef.value.muted = true
  const promise = videoRef.value.play()
  if (promise) promise.then(() => coverRef.value?.classList.add('hide')).catch(() => {})
})

const isSoundOn = ref(false)
const videoSrc = ref('')
const isCoverLoaded = ref(false)
const isCoverError = ref(false)
const isFiveSecLoaded = ref(false)
const hlsInstance = ref(null)
const onCoverLoad = () => {
  isCoverLoaded.value = true
  isCoverError.value = false
}
const onCoverError = () => {
  isCoverError.value = true
  isCoverLoaded.value = false
}

const coverSrc = ref('')
// Функция для преобразования внешних URL в локальные прокси (только в dev режиме)
const getProxiedUrl = (url) => {
  if (!url) return url

  // В production режиме возвращаем URL как есть
  if (!import.meta.env.DEV) {
    return url
  }

  // В dev режиме используем прокси
  // Если это static.chapterfour.ru, используем локальный прокси
  if (url.includes('static.chapterfour.ru')) {
    return url.replace('https://static.chapterfour.ru', '/cdn')
  }

  // Если это api.chapterfour.ru, используем локальный прокси
  if (url.includes('api.chapterfour.ru')) {
    return url.replace('https://api.chapterfour.ru', '/api')
  }

  return url
}

const isTimeout = ref(false)

async function loadAndCacheVideo() {
  if (!props.videoUrl) {
    return
  }

  try {
    const proxiedUrl = getProxiedUrl(props.videoUrl)

    // Если это HLS (.m3u8), не кэшируем, просто сохраняем URL
    if (props.videoUrl.includes('.m3u8')) {
      videoSrc.value = proxiedUrl
      return
    }

    const cache = await caches.open('video-cache')
    const cachedResponse = await cache.match(proxiedUrl)
    const response = cachedResponse || (await fetch(proxiedUrl))
    if (!cachedResponse) {
      cache.put(proxiedUrl, response.clone())
    }
    const blob = await response.blob()
    videoSrc.value = URL.createObjectURL(blob)
  } catch (error) {
    // Используем оригинальный URL как fallback
    videoSrc.value = props.videoUrl
  }
}

async function loadAndCacheCover() {
  if (!props.coverImage) return

  try {
    const proxiedUrl = getProxiedUrl(props.coverImage)

    const cache = await caches.open('cover-cache')
    const cachedResponse = await cache.match(proxiedUrl)
    const response = cachedResponse || (await fetch(proxiedUrl))
    if (!cachedResponse) {
      cache.put(proxiedUrl, response.clone())
    }
    const blob = await response.blob()
    coverSrc.value = URL.createObjectURL(blob)
  } catch (error) {
    // Используем оригинальный URL как fallback
    coverSrc.value = props.coverImage
  }
}

const playVideo = () => {
  if (!videoRef.value || !isLoaded.value) return
  if (props.disablePauseOnHover) return
  coverRef.value?.classList.add('hide')
  setTimeout(() => {
    if (videoRef.value && typeof videoRef.value.play === 'function') {
      const playPromise = videoRef.value.play()
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {})
      }
    }
  }, 200)
}

const stopVideo = () => {
  if (!videoRef.value) return
  if (props.disablePauseOnHover) return
  coverRef.value?.classList.remove('hide')
  setTimeout(() => {
    videoRef.value.pause()
    videoRef.value.currentTime = 0
  }, 200)
}

const handleVideoProgress = () => {
  if (!videoRef.value) return
  const buffered = videoRef.value.buffered
  if (buffered.length > 0) {
    const fiveSec = Math.min(5, videoRef.value.duration || 5)
    if (buffered.end(0) >= fiveSec) {
      isFiveSecLoaded.value = true
    }
  }
}

const handleVideoLoaded = () => {
  isLoaded.value = true
  if (props.autoplay && isCoverLoaded.value && !isCoverError.value) {
    handleAutoplay()
  }
}

const handleVideoCanPlay = () => {
  if (props.autoplay && isCoverLoaded.value && !isCoverError.value) {
    handleAutoplay()
  }
}

const handleAutoplay = () => {
  // Если обложка не загрузилась или произошла ошибка, не автозапускаем
  if (!isCoverLoaded.value || isCoverError.value) {
    return
  }

  if (videoRef.value && typeof videoRef.value.play === 'function') {
    videoRef.value.muted = true
    videoRef.value.playsInline = true
    videoRef.value.autoplay = true
    const playPromise = videoRef.value.play()
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {})
    }
  }
}

const toggleSound = () => {
  isSoundOn.value = !isSoundOn.value
  videoRef.value.volume = isSoundOn.value ? 1 : 0

  setTimeout(() => {
    videoRef.value.muted = !isSoundOn.value
  }, 0)
}

// Ленивая инициализация медиа: тяжёлый fetch видео/обложки запускается только когда
// плеер попадает во вьюпорт (см. onMounted). Иначе на страницах с десятками карточек
// все медиа грузятся одновременно и запросы к CDN отваливаются по таймауту (ERR_TIMED_OUT).
async function setupMedia() {
  if (mediaStarted) return
  mediaStarted = true

  setTimeout(() => {
    isTimeout.value = true
  }, 7000)

  try {
    await Promise.all([loadAndCacheVideo(), loadAndCacheCover()])
  } catch (error) {
    // Ошибка при загрузке медиа
    if (error && props.coverImage) {
      // Если обложка не загрузилась, помечаем как ошибку
      isCoverError.value = true
    }
  }

  // Таймаут для проверки загрузки обложки
  if (props.coverImage) {
    setTimeout(() => {
      if (!isCoverLoaded.value) {
        isCoverError.value = true
      }
    }, 5000) // 5 секунд таймаут для загрузки обложки
  }

  if (videoRef.value && props.videoUrl) {
    // Проверяем, это HLS-видео или обычное
    if (props.videoUrl.includes('.m3u8')) {
      // Инициализация HLS
      if (Hls.isSupported()) {
        hlsInstance.value = new Hls({
          enableWorker: true,
          lowLatencyMode: true,
          backBufferLength: 90,
        })
        hlsInstance.value.loadSource(videoSrc.value)
        hlsInstance.value.attachMedia(videoRef.value)

        hlsInstance.value.on(Hls.Events.MANIFEST_PARSED, () => {
          handleVideoLoaded()
          if (props.autoplay && isCoverLoaded.value && !isCoverError.value) {
            coverRef.value?.classList.add('hide')
            handleAutoplay()
          }
        })

        hlsInstance.value.on(Hls.Events.ERROR, (event, data) => {
          if (data.fatal) {
            switch (data.type) {
              case Hls.ErrorTypes.NETWORK_ERROR:
                hlsInstance.value.startLoad()
                break
              case Hls.ErrorTypes.MEDIA_ERROR:
                hlsInstance.value.recoverMediaError()
                break
              default:
                hlsInstance.value.destroy()
                break
            }
          }
        })
      } else if (videoRef.value.canPlayType('application/vnd.apple.mpegurl')) {
        // Нативная поддержка HLS (Safari)
        videoRef.value.src = videoSrc.value
        videoRef.value.addEventListener('loadeddata', handleVideoLoaded)
        if (props.autoplay && isCoverLoaded.value && !isCoverError.value) {
          coverRef.value?.classList.add('hide')
          handleAutoplay()
        }
      }
    } else {
      // Обычное видео (mp4, webm и т.д.)
      videoRef.value.src = videoSrc.value
      videoRef.value.addEventListener('loadeddata', handleVideoLoaded)
      if (props.autoplay && isCoverLoaded.value && !isCoverError.value) {
        coverRef.value?.classList.add('hide')
        handleAutoplay()
      }
    }

    videoRef.value.addEventListener('progress', handleVideoProgress)
  }
}

onMounted(() => {
  // Загружаем медиа заранее, чуть до появления в кадре (rootMargin), одним срабатыванием.
  // Без IntersectionObserver (например, в тестовой среде) грузим сразу.
  if (rootRef.value && typeof IntersectionObserver !== 'undefined') {
    lazyObserver.value = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          lazyObserver.value?.disconnect()
          lazyObserver.value = null
          setupMedia()
        }
      },
      { rootMargin: '300px' },
    )
    lazyObserver.value.observe(rootRef.value)
  } else {
    setupMedia()
  }

  if (
    props.videoUrl &&
    props.size !== 'video' &&
    isTouchDevice() &&
    rootRef.value &&
    typeof IntersectionObserver !== 'undefined'
  ) {
    scrollObserver.value = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        isInView.value = entry.isIntersecting
        if (!entry.isIntersecting && videoRef.value) {
          coverRef.value?.classList.remove('hide')
          videoRef.value.pause()
          videoRef.value.currentTime = 0
        }
      },
      { threshold: 0.5 },
    )
    scrollObserver.value.observe(rootRef.value)
  }
})

onUnmounted(() => {
  if (videoRef.value) {
    videoRef.value.removeEventListener('loadeddata', handleVideoLoaded)
    videoRef.value.removeEventListener('progress', handleVideoProgress)
  }

  if (hlsInstance.value) {
    hlsInstance.value.destroy()
    hlsInstance.value = null
  }

  if (scrollObserver.value) {
    scrollObserver.value.disconnect()
    scrollObserver.value = null
  }

  if (lazyObserver.value) {
    lazyObserver.value.disconnect()
    lazyObserver.value = null
  }
})
</script>

<template>
  <div ref="rootRef" class="video-player" :class="[`video-player--${size}`]">
    <div class="video-player__aspect-ratio">
      <!-- Интерактивный слой -->
      <div class="video-player__overlay" @mouseover="playVideo" @mouseleave="stopVideo">
        <div v-if="title || category" class="video-player__text">
          <!-- <p class="text-white text-title">{{ title }}</p> -->
          <!--                    <p class="text-white text-desc">{{ category }}</p>-->
        </div>
        <!-- Кнопка управления звуком -->
        <div class="volume-control" @click="toggleSound">
          <svg
            v-if="isSoundOn"
            width="16"
            height="13"
            viewBox="0 0 16 13"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M6.70971 0.0619094C6.65966 0.0930113 5.78694 0.904771 4.77033 1.86271L2.92478 3.60441H1.61726C0.491164 3.60752 0.29097 3.61374 0.203384 3.65729C-0.00932244 3.75681 6.16926e-05 3.63241 6.16926e-05 6.5C6.16926e-05 9.36759 -0.00932244 9.24319 0.203384 9.34271C0.29097 9.38626 0.491164 9.39248 1.61726 9.39559H2.92478L4.77033 11.1373C5.78694 12.0952 6.65966 12.907 6.70971 12.9381C6.91616 13.0718 7.19456 12.9785 7.25712 12.7577C7.30091 12.5991 7.30091 0.40092 7.25712 0.2423C7.19456 0.0214769 6.91616 -0.0718287 6.70971 0.0619094Z"
              fill="white"
            />
            <path
              d="M13.2473 0.282731C13.1222 0.316943 12.5216 0.917209 12.4872 1.04162C12.4434 1.19402 12.4966 1.30598 12.7875 1.65743C13.7572 2.82375 14.3546 4.21401 14.511 5.66647C14.5548 6.05524 14.5548 6.94475 14.511 7.33353C14.3546 8.78599 13.7572 10.1762 12.7875 11.3426C12.3621 11.8557 12.3652 11.9024 12.8313 12.372C13.288 12.8261 13.3912 12.8386 13.729 12.4809C14.5267 11.6349 15.2712 10.3069 15.6403 9.05968C15.8999 8.18572 16 7.4766 16 6.5C16 5.5234 15.8999 4.81428 15.6403 3.94031C15.2712 2.69313 14.5267 1.36508 13.729 0.519105C13.5163 0.295171 13.4068 0.242298 13.2473 0.282731Z"
              fill="white"
            />
            <path
              d="M11.1828 2.33548C11.1234 2.35103 10.9576 2.49099 10.7512 2.69937C10.429 3.02594 10.4165 3.04149 10.4165 3.17523C10.4165 3.29031 10.4415 3.34318 10.5603 3.49869C10.9576 4.01498 11.2548 4.55616 11.4143 5.03824C11.5801 5.54831 11.6176 5.82511 11.6176 6.50002C11.6176 7.04742 11.6082 7.18116 11.5488 7.44863C11.3768 8.21996 11.0952 8.80156 10.5603 9.50136C10.4415 9.65687 10.4165 9.70974 10.4165 9.82482C10.4165 9.95855 10.429 9.97411 10.7512 10.3007C11.2485 10.8014 11.358 10.8107 11.7209 10.366C12.0399 9.97099 12.2401 9.65376 12.481 9.15924C12.9126 8.27283 13.0878 7.50462 13.0878 6.50002C13.0878 5.76913 12.9971 5.20308 12.775 4.52505C12.6655 4.19537 12.3089 3.46759 12.1056 3.15657C11.8741 2.80823 11.5113 2.36658 11.4362 2.34481C11.308 2.3106 11.2735 2.30749 11.1828 2.33548Z"
              fill="white"
            />
            <path
              d="M9.08704 4.39752C8.94002 4.46283 8.36446 5.06621 8.33943 5.1844C8.32067 5.26526 8.34569 5.33369 8.47707 5.58872C8.66162 5.94328 8.72418 6.17344 8.72418 6.50001C8.72418 6.82658 8.66162 7.05673 8.47707 7.41129C8.34569 7.66633 8.32067 7.73475 8.33943 7.81562C8.36759 7.94625 8.97756 8.56828 9.12145 8.61494C9.34979 8.69269 9.5844 8.46876 9.85341 7.91825C10.1287 7.3522 10.235 6.76437 10.1693 6.17655C10.1193 5.74734 10.038 5.4581 9.85341 5.08176C9.67198 4.71165 9.48117 4.45972 9.33415 4.39752C9.20903 4.34465 9.2059 4.34465 9.08704 4.39752Z"
              fill="white"
            />
          </svg>
          <svg v-else width="17" height="14" viewBox="0 0 17 14" xmlns="http://www.w3.org/2000/svg">
            <path
              d="M0.59098 0.0534523C0.347101 0.156952 0.222035 0.406412 0.303328 0.624026C0.334595 0.706294 0.700413 1.03272 2.1887 2.29859L4.03968 3.87231H3.86459C3.6082 3.87231 3.33931 3.92008 3.17047 3.9997C2.97349 4.08993 2.75775 4.28896 2.65457 4.47739L2.57015 4.62866V6.79153V8.9544L2.66708 9.1269C2.78902 9.34716 2.98287 9.5117 3.24238 9.6152L3.44562 9.69747L4.90889 9.70543L6.37216 9.71339L9.17052 11.6109C10.7088 12.6565 12.0158 13.5269 12.0752 13.5482C12.2128 13.5959 12.3472 13.5959 12.4879 13.5455C12.638 13.4924 12.7099 13.4367 12.7818 13.3146C12.8381 13.2244 12.8412 13.1474 12.8412 12.2982C12.8412 11.794 12.8506 11.3827 12.8631 11.3827C12.8756 11.3827 13.4634 11.871 14.17 12.4681C15.4301 13.5349 15.4551 13.5561 15.5989 13.5721C15.8709 13.6066 16.1023 13.5084 16.2149 13.3146C16.293 13.1766 16.2993 13.0625 16.2336 12.9272C16.1648 12.7892 1.24758 0.119798 1.06936 0.0481446C0.903646 -0.0182011 0.756693 -0.0155473 0.59098 0.0534523Z"
              fill="white"
            />
            <path
              d="M12.0721 0.0348503C11.9876 0.0666963 7.11943 3.33887 6.94121 3.48483L6.8693 3.54056L9.85526 6.07497L12.8412 8.60938V4.48798C12.8412 0.496617 12.8381 0.363925 12.7818 0.268387C12.7099 0.146311 12.638 0.0905807 12.4879 0.0375041C12.3503 -0.0102648 12.2002 -0.0129186 12.0721 0.0348503Z"
              fill="white"
            />
          </svg>
        </div>
      </div>

      <!-- Лоадер + skeleton -->
      <div
        v-if="coverImage && (!isFiveSecLoaded || !isCoverLoaded) && !isTimeout"
        class="video-loader"
      >
        <div class="skeleton-cover"></div>
      </div>

      <!-- Обложка -->
      <div v-if="coverImage" class="video-player__cover" ref="coverRef">
        <img
          :src="coverSrc || coverImage"
          :alt="title || ''"
          loading="lazy"
          decoding="async"
          @load="onCoverLoad"
          @error="onCoverError"
        />
      </div>

      <!-- Видео -->
      <video
        v-if="videoUrl"
        ref="videoRef"
        :src="videoSrc"
        muted
        :autoplay="props.autoplay && isCoverLoaded && !isCoverError"
        loop
        playsinline
        preload="metadata"
        class="video-player__video"
        @canplay="handleVideoCanPlay"
      ></video>
    </div>
  </div>
</template>

<style scoped>
.video-player {
  overflow: hidden;
}

.video-player--video .video-player__aspect-ratio {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  padding: 0;
}

.video-player--video .video-player__video,
.video-player--video .video-player__cover,
.video-player--video .video-player__overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.video-player__overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 3;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  /* background: linear-gradient(180deg, rgba(255, 255, 255, 0) 60%, #000000b3); */
}

.video-player__aspect-ratio {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  overflow: hidden;
  background: #000;
}

.video-player__text {
  padding: 1rem;
}

.video-player__text p {
  margin: 0;
  text-decoration: none;
}

.video-player__cover {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2;
  opacity: 1;
  transition: opacity 0.8s ease;
}

.video-player__cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center;
  transform: scale(1.01);
}

.video-player__video {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 1;
  object-fit: cover;
  object-position: top center;
  transform: scale(1.01);
}

.video-player--video .video-player__cover img {
  height: 100dvh;
  object-fit: cover;
  object-position: center;
}

.volume-control {
  display: none;
}

.hero-video .volume-control {
  cursor: pointer;
  position: absolute;
  bottom: 1.875rem;
  left: 1.875rem;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: rgba(0, 0, 0, 0.7);
  border-radius: 50%;
  padding: 0.75rem;
}

.hero-video .volume-control svg {
  display: block;
  width: 100%;
  height: 100%;
}

.text-title {
  font-family: Montserrat, Arial, sans-serif;
  font-size: 1.5rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.06rem;
  padding-bottom: 0.1rem;
}

.text-desc {
  font-family: Montserrat, Arial, sans-serif;
  font-size: 0.875rem;
  font-weight: 400;
  text-transform: uppercase;
  margin-top: 0.25rem;
  letter-spacing: 0.035rem;
}

@media (max-width: 87.5rem) {
  .text-title {
    font-size: 1.5rem;
  }

  .text-desc {
    font-size: 0.75rem;
  }
}

@media (max-width: 48rem) {
  .text-title {
    font-size: 1.25rem;
  }

  .text-desc {
    font-size: 0.625rem;
  }

  .hero-video .volume-control {
    bottom: 3.125rem;
    left: 1.25rem;
    width: 1.5rem;
    height: 1.5rem;
  }
}

@media (max-width: 36rem) {
  .text-title {
    font-size: 1.375rem;
  }

  .text-desc {
    font-size: 0.75rem;
  }
}

@media (max-width: 20rem) {
  .text-title {
    font-size: 1.125rem;
  }

  .text-desc {
    font-size: 0.625rem;
  }
}

.video-loader {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  /* background-color: rgba(0, 0, 0, 0.5); */ /* убираем затемнение */
  z-index: 4;
  overflow: hidden;
  border-radius: inherit;
}

.spinner {
  border: 0.25rem solid rgba(255, 255, 255, 0.3);
  border-top: 0.25rem solid #fff;
  border-radius: 50%;
  width: 2.5rem;
  height: 2.5rem;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}
.skeleton-cover {
  width: 100%;
  height: 100%;
  border-radius: 0;
  background: linear-gradient(90deg, #ececec 25%, #f5f5f5 50%, #ececec 75%);
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.2s infinite linear;
}
@keyframes skeleton-shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}
</style>
